/**
 * A block, in the vocabulary the generator and the bot emit.
 *
 * The creator's app stores six JSON files per block and the platform corpus
 * stores the same six under different names. The v4 schema vocabulary is
 * canonical here — a screen built on it takes a generated or bot-authored
 * block without translation, which is the point of speaking one language
 * across the chat surface and the editor.
 *
 * The disk names are absorbed in exactly one place: this module. Nothing
 * downstream should ever see `state.json` or `data.json`.
 */
export interface BlockParts {
  /** What this block is — name, type, recipe. (`meta.json`) */
  block: unknown
  /** What is shown — the component tree. (`ui.json`) */
  ui: unknown
  /** What it reacts to — element bindings. (`events.json`) */
  events: unknown
  /** What happens — the flows those bindings run. (`flow.json`) */
  flow: unknown
  /** What data it uses. (`data.json`) */
  datasource: unknown
  /** What it remembers. (`state.json`) */
  variables: unknown
}

/**
 * Disk filename → the name the schema gives that part.
 *
 * Kept in lockstep with `template-loader.ts`, which is what writes these keys:
 * a test derives the accepted names from that file's own fetches, so renaming
 * one there reds rather than silently dropping a part.
 */
export const DISK_TO_SCHEMA = {
  meta: 'block',
  ui: 'ui',
  events: 'events',
  flow: 'flow',
  data: 'datasource',
  state: 'variables',
} as const satisfies Record<string, keyof BlockParts>

/**
 * The six parts of one block, from the bundle `template-loader` produces.
 *
 * `ui` is the one part the loader requires — it warns and returns without a
 * block when `ui.json` is missing — so a block with no ui is not something
 * this can show, and it says so instead of returning six undefineds. Absent
 * optional parts stay absent: "this block has no flow" is a fact worth
 * showing, and an empty object would hide it.
 */
export function blockPartsFromBundleFiles(
  files: Record<string, unknown>,
  blockName: string,
): BlockParts | null {
  const at = (disk: string) => files[`blocks/${blockName}/${disk}.json`]
  if (at('ui') === undefined) return null

  const parts = {} as Record<keyof BlockParts, unknown>
  for (const [disk, schema] of Object.entries(DISK_TO_SCHEMA)) {
    parts[schema as keyof BlockParts] = at(disk)
  }
  return parts as unknown as BlockParts
}
